import React, { CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSafeNavigate } from "../../../../../utils/useSafeNavigate";
import Swal, { type SweetAlertIcon, type SweetAlertOptions } from "sweetalert2";
import CustomPagination from "../../../../admin/common/custompagination";
import TableLoader from "../../../../../utils/TableLoader";
import RecordNotFound from "../../../shared/recordNotFound";
import {
  ACTIVE,
  DEACTIVE,
  LOADER_TIMEOUT_TIME,
  SORT_D,
  SWAL_SETTINGS,
} from "../../../../../utils/Constants";
import { changeStatus, list, removeCustomer } from "../../../services/customer.service";
import { handleServerValidations, TrimText } from "../../../../../utils/commonfunction";

interface Customer {
  _id: string;
  customer_phone_number?: string | null;
  customer_name?: string | null;
  line_user_id?: string | null;
  status?: boolean | number | null;
}

interface CustomerListData {
  list?: Customer[];
  page?: number;
  offset?: number;
  total_records?: number;
}

interface ServiceResponse<T = unknown> {
  success?: boolean;
  message?: string;
  data?: T;
}

interface CustomerStatusPayload {
  o_id: string;
  status: boolean | number;
}

interface CustomerDeletePayload {
  o_id: string;
}

interface PaginationChange {
  currentPage: number;
}

const actionCellStyle: CSSProperties = {
  minWidth: "180px",
  position: "relative",
  zIndex: 1,
};

const actionButtonStyle: CSSProperties = {
  position: "relative",
  zIndex: 2,
};

export default function Customers(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useSafeNavigate();
  const params = useParams<"page">();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loader, setLoader] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [offset, setOffset] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const itemPerPage = 10;

  const isActiveCustomer = (status?: boolean | number | null): boolean =>
    Boolean(status) === Boolean(ACTIVE);

  const showResultAlert = (icon: SweetAlertIcon, text: unknown): void => {
    void Swal.fire({
      ...(SWAL_SETTINGS as SweetAlertOptions),
      icon,
      text: typeof text === "string" ? text : String(text ?? ""),
    });
  };

  useEffect(() => {
    setLoader(true);
    const formData = new FormData();
    formData.append("page", params.page || "1");
    formData.append("per_page", String(itemPerPage));
    formData.append("sort", JSON.stringify({ createdAt: SORT_D }));

    list(formData)
      .then((response: ServiceResponse<CustomerListData>) => {
        const data = response?.data;
        setCustomers(data?.list || []);
        setPage(data?.page || 1);
        setOffset(data?.offset || 0);
        setTotalRecords(data?.total_records || 0);
        navigate(`/restaurant/settings/customers/${data?.page || 1}`);
      })
      .catch(() => {
        setCustomers([]);
      })
      .finally(() => {
        window.setTimeout(() => setLoader(false), LOADER_TIMEOUT_TIME);
      });
  }, [itemPerPage, navigate, params.page]);

  const updateStatus = (item: Customer): void => {
    const nextStatus = isActiveCustomer(item.status) ? DEACTIVE : ACTIVE;
    const payload: CustomerStatusPayload = { o_id: item._id, status: nextStatus };

    changeStatus(payload).then((response: ServiceResponse) => {
      showResultAlert(response?.success ? "success" : "error", handleServerValidations(response));

      if (response?.success) {
        setCustomers((prev) =>
          prev.map((customer) =>
            customer._id === item._id ? { ...customer, status: nextStatus } : customer
          )
        );
      }
    });
  };

  const deleteRecord = (item: Customer): void => {
    Swal.fire({
      title: t("msg_are_you_sure"),
      text: t("msg_remove_line_customer"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("remove"),
      cancelButtonText: t("btn_cancel"),
      customClass: {
        popup: "swal-restaurant",
        confirmButton: "btn btn-danger col",
        cancelButton: "btn btn-secondary col",
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      const payload: CustomerDeletePayload = { o_id: item._id };
      removeCustomer(payload).then((response: ServiceResponse) => {
        showResultAlert(
          response?.success ? "success" : "error",
          handleServerValidations(response)
        );

        if (response?.success) {
          setCustomers((prev) => prev.filter((customer) => customer._id !== item._id));
          setTotalRecords((prev) => Math.max(0, prev - 1));
        }
      });
    });
  };

  return (
    <main id="main-content">
      <div className="inner-container">
        <div className="content">
          <div className="table-card" style={{ marginTop: "12px" }}>
            <div
              className="table-card-heading d-flex justify-content-between align-items-center flex-wrap gap-2"
              style={{ paddingBottom: "10px" }}
            >
              <h4 className="mb-0">{t("customers_on_line")}</h4>
              <small className="text-muted">
                {t("label_total_customers")}: <strong>{totalRecords}</strong>
              </small>
            </div>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("label_serial_number")}</th>
                    <th>{t("customer_phone")}</th>
                    <th>{t("line_user")}</th>
                    <th style={{ minWidth: "180px" }}>{t("label_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={4}>
                        <TableLoader />
                      </td>
                    </tr>
                  ) : customers.length ? (
                    customers.map((item, index) => (
                      <tr key={item._id}>
                        <td>{offset + index + 1}</td>
                        <td>{item.customer_phone_number || t("label_not_entered")}</td>
                        <td>
                          {item.line_user_id ? (
                            <>
                              <span className="d-block">
                                {TrimText(item.customer_name, 20) || t("label_line_customer")}
                              </span>
                            </>
                          ) : (
                            t("label_not_entered")
                          )}
                        </td>
                        <td style={actionCellStyle}>
                          <div className="customer-actions">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                isActiveCustomer(item.status) ? "btn-danger" : "btn-success"
                              }`}
                              onClick={() => updateStatus(item)}
                              style={actionButtonStyle}
                            >
                              {isActiveCustomer(item.status) ? t("btn_block") : t("btn_unblock")}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm customer-action-remove"
                              onClick={() => deleteRecord(item)}
                              style={actionButtonStyle}
                            >
                              {t("remove")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <RecordNotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalRecords > itemPerPage ? (
              <CustomPagination
                page={page}
                totalRecords={totalRecords}
                itemsPerPage={itemPerPage}
                onPageChanged={(pageData: PaginationChange) =>
                  navigate(`/restaurant/settings/customers/${pageData.currentPage}`)
                }
              />
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}
